import type { PropsWithChildren, ReactNode } from 'react'
import { PlayerAvatar, Text, ThemedBackgroundHeader } from '@mote/ui'
import type { HTMLXStyledProps } from '@mote/ui/system'
import { forwardRef } from '@mote/ui/system'
import type { SystemProps } from '@xstyled/styled-components'
import { useAuth } from '../../auth'
import {
  StyledCreatorData,
  StyledLandingMainLayout,
  StyledLandingMainLayoutContent,
  StyledTitle
} from './LandingMainLayout.styles'

export interface LandingMainLayoutProps
  extends Omit<
    HTMLXStyledProps<'div'>,
    'gridTemplateRows' | 'background' | 'title'
  > {
  gridTemplateRows: SystemProps['gridTemplateRows']
  headerContent?: ReactNode
  footerContent?: ReactNode
  isAnonymous: boolean
  avatarSrc: string
  creatorName: string
  createdDate: string
  title: ReactNode
  headerBackground: 'neutral' | 'classic' | 'mulberry'
}

export const LandingMainLayout = forwardRef<
  PropsWithChildren<LandingMainLayoutProps>,
  'div'
>((props, ref): JSX.Element => {
  const {
    children,
    gridTemplateRows,
    isAnonymous,
    headerContent,
    footerContent,
    headerBackground,
    creatorName,
    createdDate,
    avatarSrc,
    title,
    ...stylingProps
  } = props
  const auth = useAuth()

  return (
    <StyledLandingMainLayout
      {...stylingProps}
      ref={ref}
      gridTemplateRows={gridTemplateRows}
      isAuthenticated={auth.isAuthenticated}
    >
      <ThemedBackgroundHeader
        isAuthenticated={auth.isAuthenticated}
        background={headerBackground}
      >
        {headerContent}
      </ThemedBackgroundHeader>
      <StyledLandingMainLayoutContent>
        <PlayerAvatar
          isAnonymous={isAnonymous}
          avatarSrc={avatarSrc}
          name={creatorName}
        />
        <StyledCreatorData>
          <Text
            tag={'span'}
            fontSize={16}
            fontWeight={700}
            lineHeight={'150%'}
            color='grey.d3'
          >
            {creatorName}
          </Text>
          <Text
            tag={'span'}
            fontSize={14}
            fontWeight={400}
            lineHeight={'143%'}
            color='grey.d2'
          >
            {createdDate}
          </Text>
        </StyledCreatorData>
        <StyledTitle>{title}</StyledTitle>
        {children}
      </StyledLandingMainLayoutContent>
      {footerContent}
    </StyledLandingMainLayout>
  )
})
